import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { formatEuro } from "@/lib/utils";
import { DateRange } from "./DateFilter";
import { TrendingUp } from "lucide-react";
import { useWorkspace } from "@/hooks/useWorkspace";

interface TopProductsProps {
  dateRange: DateRange;
}

interface ProductData {
  id: string;
  name: string;
  quantity: number;
  revenue: number;
}

export function TopProducts({ dateRange }: TopProductsProps) {
  const [products, setProducts] = useState<ProductData[]>([]);
  const [loading, setLoading] = useState(true);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;

  useEffect(() => {
    if (workspaceId) fetchData();
  }, [dateRange, workspaceId]);

  const fetchData = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data: orderItems } = await supabase
        .from("order_items")
        .select(`
          quantity, total, product_id,
          products(id, name),
          orders!inner(order_date, status, workspace_id)
        `)
        .eq("orders.workspace_id", workspaceId)
        .gte("orders.order_date", dateRange.from.toISOString().split("T")[0])
        .lte("orders.order_date", dateRange.to.toISOString().split("T")[0])
        .neq("orders.order_type", "purchase")
        .in("orders.status", ["completed", "delivered", "shipped"]);

      const productStats: Record<string, { name: string; quantity: number; revenue: number }> = {};

      orderItems?.forEach((item) => {
        const productId = item.product_id;
        const productName = (item.products as any)?.name || "Producto";
        if (productId) {
          if (!productStats[productId]) productStats[productId] = { name: productName, quantity: 0, revenue: 0 };
          productStats[productId].quantity += Number(item.quantity) || 0;
          productStats[productId].revenue += Number(item.total) || 0;
        }
      });

      const topProducts = Object.entries(productStats)
        .map(([id, stats]) => ({ id, ...stats }))
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 5);

      setProducts(topProducts);
    } catch (error) {
      console.error("Error fetching top products:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="chart-container">
        <div className="mb-4">
          <h3 className="font-display text-lg font-semibold text-foreground">Productos Más Vendidos</h3>
          <p className="text-sm text-muted-foreground">Top 5 por cantidad vendida</p>
        </div>
        <div className="space-y-3">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      </div>
    );
  }

  return (
    <div className="chart-container animate-slide-in-up">
      <div className="mb-4">
        <h3 className="font-display text-lg font-semibold text-foreground">Productos Más Vendidos</h3>
        <p className="text-sm text-muted-foreground">Top 5 por cantidad vendida</p>
      </div>
      {products.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-muted-foreground">No hay ventas en este período</div>
      ) : (
        <div className="space-y-3">
          {products.map((product, index) => (
            <div key={product.id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-primary font-bold text-sm">{index + 1}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
                <p className="text-xs text-muted-foreground">{product.quantity} unidades vendidas</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground">{formatEuro(product.revenue, false)}</p>
                <div className="flex items-center gap-1 text-success text-xs"><TrendingUp className="h-3 w-3" /></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
